import { PayloadSchemaProperty, TelemetryEntry, TmState, ValidationRule } from "./TestCaseTypes";
import { reconstructParamStates } from "./TestCaseHelpers";

type CatalogMatchStatus = "Matched" | "Mismatched" | "Unknown";

export interface ValidationConfigRule {
  isValidate: boolean;
  type?: "constant" | "range" | "enumMatch" | "equalto" | "equal" | "equal_to" | "allElementsInArraySatisfy";
  value?: number | string;
  min?: number | string;
  max?: number | string;
  element?: unknown;
}

export interface ValidationConfig {
  validationConfigId: string;
  validationName: string;
  commandId: number;
  commandName: string;
  saId: number;
  daId: number;
  mode: number | null;
  subsystem: string;
  description: string;
  version: string;
  tmCatalogVersion: string;
  type: "Default" | "Custom";
  catalogMatchStatus: CatalogMatchStatus;
  validations?: {
    payload?: Record<string, ValidationConfigRule>;
  };
  tmPayloadSchema?: {
    type?: "object";
    properties?: Record<string, PayloadSchemaProperty>;
  };
}

type ValidationConfigTelemetryEntry = TelemetryEntry & {
  validationConfig?: ValidationConfig;
};


// Matched  → use the stored validation rules (has isValidate, type, min/max/value)
// Mismatched/Unknown → fall back to the raw TM schema properties for display
export const getPayloadSource = (
  validationConfig: ValidationConfig
): Record<string, ValidationConfigRule | PayloadSchemaProperty> => {
  const validationsPayload = validationConfig.validations?.payload;
  if (validationsPayload && typeof validationsPayload === "object" && Object.keys(validationsPayload).length > 0) {
    return validationsPayload;
  }
  const schema =
    validationConfig.tmPayloadSchema ||
    (validationConfig as any).payloadSchema ||
    (validationConfig as any).annotations?.payloadSchema;
  return schema?.properties || {};
};

const getPropertyTypeFromRule = (rule: ValidationConfigRule | null | undefined): PayloadSchemaProperty["type"] => {
  if (!rule) return "string";

  try {
    const numericValue = rule.value;
    if (typeof numericValue === "number") {
      return Number.isInteger(numericValue) ? "integer" : "number";
    }
    if (typeof rule.min === "number" || typeof rule.max === "number") {
      const minIsInteger = rule.min == null || Number.isInteger(rule.min);
      const maxIsInteger = rule.max == null || Number.isInteger(rule.max);
      return minIsInteger && maxIsInteger ? "integer" : "number";
    }
    return "string";
  } catch {
    return "string";
  }
};

const getPayloadSchemaProperties = (validationConfig: ValidationConfig): Record<string, PayloadSchemaProperty> => {
  const payloadSource = getPayloadSource(validationConfig);

  if (validationConfig.catalogMatchStatus === "Mismatched" || validationConfig.catalogMatchStatus === "Unknown") {
    const properties = payloadSource as Record<string, PayloadSchemaProperty>;
    if (!properties || typeof properties !== "object") {
      console.warn("Mismatched or Unknown config has invalid schema properties:", validationConfig.validationConfigId);
      return {};
    }
    return properties;
  }

  try {
    return Object.entries(payloadSource as Record<string, ValidationConfigRule>).reduce<Record<string, PayloadSchemaProperty>>((acc, [key, rule]) => {
      const schemaProperty = validationConfig.tmPayloadSchema?.properties?.[key];
      const inferredType = getPropertyTypeFromRule(rule);

      acc[key] = {
        ...schemaProperty,
        type: schemaProperty?.type ?? inferredType,
      };
      return acc;
    }, {});
  } catch (error) {
    console.error("Error building payload schema properties:", error);
    return {};
  }
};

const getPayloadTemplate = (validationConfig: ValidationConfig): Record<string, unknown> => {
  try {
    const payloadSource = getPayloadSource(validationConfig);
    if (!payloadSource || typeof payloadSource !== "object") {
      console.warn("Payload source is not an object:", validationConfig.validationConfigId);
      return {};
    }
    return Object.keys(payloadSource).reduce<Record<string, unknown>>((acc, key) => {
      const rule = (payloadSource as Record<string, any>)[key];
      // Extract value from validation rule - use min for range type, value for others
      if (rule?.value !== undefined && rule?.value !== null && rule?.value !== "") {
        acc[key] = rule.value;
      } else if (rule?.min !== undefined && rule?.min !== null) {
        acc[key] = rule.min;
      } else if (rule?.max !== undefined && rule?.max !== null) {
        acc[key] = rule.max;
      } else {
        acc[key] = "";
      }
      return acc;
    }, {});
  } catch (error) {
    console.error("Error building payload template:", error);
    return {};
  }
};

// Returns null (never throws) so callers can safely .map().filter(Boolean).
// subsystem is allowed to be empty — some commands have no subsystem in the catalog.
export const toTelemetryEntry = (validationConfig: ValidationConfig): ValidationConfigTelemetryEntry | null => {
  try {
    if (
      !validationConfig.commandId ||
      !validationConfig.commandName ||
      validationConfig.saId == null ||
      validationConfig.daId == null
    ) {
      console.warn("ValidationConfig has missing critical fields:", validationConfig.validationConfigId);
      return null;
    }

    const rawSchema =
      validationConfig.tmPayloadSchema ||
      (validationConfig as any).payloadSchema ||
      (validationConfig as any).annotations?.payloadSchema;

    return {
      commandId: validationConfig.commandId,
      commandName: validationConfig.commandName,
      saId: validationConfig.saId,
      daId: validationConfig.daId,
      subsystem: validationConfig.subsystem,
      payload: getPayloadTemplate(validationConfig),
      payloadSchema: rawSchema?.properties
        ? rawSchema
        : {
            type: "object",
            properties: getPayloadSchemaProperties(validationConfig),
          },
      mode: validationConfig.mode,
      validationConfig,
    };
  } catch (error) {
    console.error("Failed to convert ValidationConfig to TelemetryEntry:", error, validationConfig.validationConfigId);
    return null;
  }
};

const toValidationRule = (rule: ValidationConfigRule): ValidationRule => {
  try {
    if (
      rule.type === "range" ||
      rule.type === "enumMatch" ||
      (!rule.type && (rule.min != null || rule.max != null))
    ) {
      const min = rule.min != null ? String(rule.min) : "0";
      const max = rule.max != null ? String(rule.max) : "0";
      return { type: rule.type === "enumMatch" ? "enumMatch" : "range", min, max, value: "0" };
    }
    const isEqualTo = rule.type === "equalto" || rule.type === "equal" || rule.type === "equal_to";
    return {
      type: isEqualTo ? "equal" : "constant",
      min: "0",
      max: "0",
      value: rule.value != null ? String(rule.value) : "0",
    };
  } catch (error) {
    console.error("Error converting validation rule:", error, rule);
    return { type: "constant", min: "0", max: "0", value: "0" };
  }
};

// Leaf = has BOTH `isValidate` and leaf rule attributes (type string, or primitive value/min/max).
// Group nodes (Sys_time_config etc.) carry a sibling `isValidate` rollup too, but contain child objects with `isValidate`.
const isLeafValidationRule = (node: unknown): node is ValidationConfigRule => {
  if (!node || typeof node !== "object" || Array.isArray(node)) return false;
  const obj = node as Record<string, unknown>;
  if (!("isValidate" in obj)) return false;

  const isValidateBool =
    obj.isValidate === true ||
    obj.isValidate === "true" ||
    obj.isValidate === 1 ||
    obj.isValidate === "1";
  const isUnchecked =
    obj.isValidate === false ||
    obj.isValidate === "false" ||
    obj.isValidate === 0 ||
    obj.isValidate === "0";

  if (!isValidateBool && !isUnchecked) return false;

  const hasStringType = typeof obj.type === "string";
  const hasPrimitiveValue = obj.value !== undefined && (obj.value === null || typeof obj.value !== "object");
  const hasPrimitiveMin = obj.min !== undefined && (obj.min === null || typeof obj.min !== "object");
  const hasPrimitiveMax = obj.max !== undefined && (obj.max === null || typeof obj.max !== "object");

  if (!hasStringType && !hasPrimitiveValue && !hasPrimitiveMin && !hasPrimitiveMax) {
    return false;
  }

  // Ensure it's not a group node that happens to contain child validation objects
  const hasChildValidationObjects = Object.values(obj).some(
    (val) => val !== null && typeof val === "object" && !Array.isArray(val) && "isValidate" in val
  );

  return !hasChildValidationObjects;
};

// Recurses through the nested payload tree, collecting every leaf with
// isValidate:true. Deliberately ignores a group's own isValidate rollup —
// that flag summarizes the group, it doesn't gate its children.
const collectCheckedValidations = (
  node: unknown,
  prefix: string,
  checkedParams: string[],
  validations: Record<string, ValidationRule>,
  arrayModes: Record<string, "all" | "indexed"> = {}
): void => {
  if (node == null || typeof node !== "object") return;

  const rawObj = node as Record<string, unknown>;
  if (rawObj.type === "allElementsInArraySatisfy") {
    arrayModes[prefix] = "all";
    const element = rawObj.element;
    if (element && typeof element === "object") {
      const elObj = element as Record<string, unknown>;
      // Check if element is a leaf primitive validation rule
      if (
        (typeof elObj.type === "string" || elObj.value !== undefined || elObj.min !== undefined || elObj.max !== undefined) &&
        !Object.values(elObj).some((val) => val !== null && typeof val === "object" && !Array.isArray(val) && ("isValidate" in val || "type" in val))
      ) {
        const isParentValidate = rawObj.isValidate !== false && String(rawObj.isValidate) !== "false";
        const isChecked =
          isParentValidate &&
          (elObj.isValidate === true ||
            String(elObj.isValidate) === "true" ||
            elObj.isValidate === 1 ||
            elObj.isValidate === "1" ||
            elObj.isValidate === undefined);
        const paramKey = `${prefix}[*]`;
        if (isChecked) {
          checkedParams.push(paramKey);
        }
        validations[paramKey] = toValidationRule(elObj as unknown as ValidationConfigRule);
        return;
      }
      // Struct element
      Object.entries(elObj).forEach(([childKey, childVal]) => {
        const subPath = `${prefix}[*].${childKey}`;
        collectCheckedValidations(childVal, subPath, checkedParams, validations, arrayModes);
      });
      return;
    }
  }

  if (isLeafValidationRule(node)) {
    const isChecked =
      node.isValidate === true ||
      String((node as any).isValidate) === "true" ||
      (node as any).isValidate === 1 ||
      (node as any).isValidate === "1";
    if (isChecked) {
      checkedParams.push(prefix);
      validations[prefix] = toValidationRule(node);
    }
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item, idx) => {
      collectCheckedValidations(item, `${prefix}[${idx}]`, checkedParams, validations, arrayModes);
    });
    return;
  }

  Object.entries(node as Record<string, unknown>).forEach(([key, value]) => {
    if (key === "isValidate") return;
    // Format numeric object keys as array indices (e.g. Data[0] instead of Data.0)
    const isNumeric = /^\d+$/.test(key);
    const childPath = isNumeric
      ? (prefix ? `${prefix}[${key}]` : `[${key}]`)
      : (prefix ? `${prefix}.${key}` : key);
    collectCheckedValidations(value, childPath, checkedParams, validations, arrayModes);
  });
};

// Extracts pre-filled validation state from a ValidationConfig-backed TelemetryEntry.
// Returns null if the entry has no attached validationConfig or no checked params.
export const getInitialTmStateFromValidationConfig = (
  telemetryEntry: unknown
): TmState | null => {
  try {
    if (!telemetryEntry || typeof telemetryEntry !== "object") return null;
    const entry = telemetryEntry as any;
    const validationConfig = entry?.validationConfig ?? entry;
    if (!validationConfig) return null;

    const checkedParams: string[] = [];
    const validations: Record<string, ValidationRule> = {};
    const arrayModes: Record<string, "all" | "indexed"> = {};

    const payload =
      validationConfig?.validations?.payload ??
      (validationConfig?.validations && !("payload" in validationConfig.validations) ? validationConfig.validations : undefined) ??
      validationConfig?.payload ??
      entry?.validation ??
      entry?.validations?.payload;

    if (payload && typeof payload === "object") {
      collectCheckedValidations(payload, "", checkedParams, validations, arrayModes);
    }

    if (checkedParams.length > 0) {
      return {
        checkedParams: Array.from(new Set(checkedParams)),
        validations,
        ...(Object.keys(arrayModes).length > 0 ? { arrayModes } : {}),
      };
    }

    // Robust fallback: use reconstructParamStates which uses BFS root discovery
    const label = entry.commandName || entry.label || validationConfig.commandName || "tm";
    const tmEntry = {
      label,
      value: entry,
      validation: (payload || validationConfig?.validations || entry?.validation || {}) as Record<string, unknown>,
    };
    const reconstructed = reconstructParamStates([tmEntry]);
    const state = reconstructed[label];
    if (state && state.checkedParams && state.checkedParams.length > 0) {
      return state;
    }

    return null;
  } catch (error) {
    console.error("Error extracting initial state from validation config:", error);
    return null;
  }
};


// Guards against malformed entries in the API response.
// subsystem is intentionally not required — some catalog entries have an empty subsystem.
// validations and tmPayloadSchema are optional fields; only their type is checked if present.
const isValidValidationConfig = (config: unknown): config is ValidationConfig => {
  if (!config || typeof config !== "object") return false;
  const c = config as Record<string, unknown>;

  if (typeof c.validationConfigId !== "string" || !c.validationConfigId) return false;
  if (typeof c.commandId !== "number" || !c.commandId) return false;
  if (typeof c.saId !== "number") return false;
  if (typeof c.daId !== "number") return false;
  if (typeof c.subsystem !== "string") return false;
  if (typeof c.commandName !== "string" || !c.commandName) return false;

  const status = String(c.catalogMatchStatus || "").toLowerCase();
  if (status !== "matched" && status !== "mismatched" && status !== "unknown") return false;

  if (c.validations !== undefined && typeof c.validations !== "object") return false;
  if (c.tmPayloadSchema !== undefined && typeof c.tmPayloadSchema !== "object") return false;

  return true;
};

export interface ValidationConfigApiResponse {
  status?: number;
  message?: string;
  recordsCount?: number;
  data: ValidationConfig[];
}

const isValidApiResponse = (response: unknown): response is ValidationConfigApiResponse => {
  if (!response || typeof response !== "object") return false;
  const r = response as Record<string, unknown>;

  // Check that data exists and is an array
  if (!Array.isArray(r.data)) return false;

  return true;
};

// Parses the raw API response from getValidationConfigs.
// The API returns { status, message, recordsCount, data: ValidationConfig[] }.
// catalogMatchStatus is normalized here because the API may return any casing (e.g. "matched", "MATCHED").
export const parseValidationConfigResponse = (
  response: unknown
): { success: boolean; configs: ValidationConfig[]; error?: string } => {
  if (!isValidApiResponse(response)) {
    return { success: false, configs: [], error: "Invalid API response structure" };
  }

  const mappedData = response.data.map((config) => {
    if (config && typeof config === "object") {
      const c = config as any;
      const status = String(c.catalogMatchStatus || "").toLowerCase();
      let catalogMatchStatus: CatalogMatchStatus = "Unknown";
      if (status === "matched") catalogMatchStatus = "Matched";
      else if (status === "mismatched") catalogMatchStatus = "Mismatched";

      return {
        ...c,
        catalogMatchStatus,
      };
    }
    return config;
  });

  const validConfigs = mappedData.filter(isValidValidationConfig);

  if (validConfigs.length === 0 && response.data.length > 0) {
    return { success: false, configs: [], error: "No valid validation configs in response" };
  }

  if (validConfigs.length < response.data.length) {
    console.warn(
      `Validation Config API: ${response.data.length - validConfigs.length} invalid configs filtered out`
    );
  }

  return { success: true, configs: validConfigs };
};

